import React, { Fragment } from 'react';
import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { CommodityType } from '@bechtel/types';
import { NetworkStatus } from '@apollo/client';
import { FieldArray, FieldProps } from 'formik';
import { findIndex, get, some } from 'lodash';
import {
  ErrorRetry,
  useAppUser,
  useProjectCommodityTypesQuery,
  ListSelectionStyled,
  SubListSelectionStyled,
} from '@bechtel/client-common';
import { Trans } from '@lingui/macro';

export const CommodityTypeList: React.FC<FieldProps> = ({ form }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useProjectCommodityTypesQuery(user?.project?.id);
  const commodityTypeList: CommodityType[] = get(data, 'projectConfigById.projectCommodityTypes', []);
  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Commodity Type List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.commodityTypes?.length !== commodityTypeList.length) {
                form.setFieldValue('commodityTypes', commodityTypeList);
              } else {
                form.setFieldValue('commodityTypes', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={form.values.commodityTypes?.length === commodityTypeList.length}
                  indeterminate={
                    form.values.commodityTypes?.length > 0 &&
                    form.values.commodityTypes?.length < commodityTypeList.length
                  }
                  readOnly
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans>Commodity Types</Trans>
              </Text>
            </Box>
            {form.values.commodityTypes && form.values.commodityTypes.length > 0 && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.commodityTypes.length} <Trans>Selected</Trans>
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="commodityTypes">
            {(arrayHelpers) => {
              return (
                <>
                  {commodityTypeList &&
                    commodityTypeList.map((commodityType: CommodityType) => {
                      return (
                        <ListSelectionStyled
                          key={commodityType.id}
                          data-testid={`block-card-search-canvas-commodity-types-${commodityType.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.commodityTypes, (item: CommodityType) => {
                              return item.id === commodityType.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(commodityType);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.commodityTypes, (item) => {
                                return item.id === commodityType.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Text size="400" color="bechtel.grey.1000">
                            {commodityType.name}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
